// The Aquaphobia sign (brand.md §1, R1644; mockup round 079, card 06 with
// the plate of card 02).
//
// The sign is the pointy-top hexagon of the map cell, filled with the deep
// gradient of the water and edged in the accent, with the fleet chevron and
// its counter in the bright accent. It draws in the 24-unit box of
// `favicon.svg`, about the centre (12, 12).
//
// This is the ONE module of the client that draws the sign. It reads the
// geometry of `[presentation.sign]` through the generated `SIGN` and turns
// it into:
//
// - `signSVG`: the inner SVG markup in the 24-unit box, and
//   `signSVGElement`: a whole `<svg>` string at a pixel size;
// - `drawSign`: a canvas draw through `Path2D`;
// - `signGeometry`, `hexPoints` and the three `sign*Path` strings: the
//   parts, for a surface that animates them.
//
// balancegen writes the same markup for the site icon and the page headers
// (`server/cmd/balancegen/sign.go`), and `sign.test.ts` holds the rendered
// files to `signSVG`. `scripts/tools/render-icon.py` reads the same table.
//
// The dev identity (brand.md §15, R2880; mockup round 135, card 09) adds
// the dev corner to the sign, and `brandIdentity` is the one reader of the
// marker in the page head that says which identity the page wears.

import { ACCENT_BRIGHT_CSS, ACCENT_CSS, SIGN } from '../gen/presentation';

/** `full` is 32 px and every size above it; `small` is the cut for 16 px. */
export type SignCut = 'full' | 'small';
/** `round`: the cell on a transparent ground (the favicon, a lockup).
 *  `square`: the cell on a full square of the page ink (a touch icon, a
 *  launcher icon, which the platform masks itself). */
export type SignShape = 'round' | 'square';
/** `colour`, or `mono`: the edge and the glyph in one ink, with no plate. */
export type SignTone = 'colour' | 'mono';
/** One point in the 24-unit box. */
export type SignPoint = readonly [number, number];

/** How to draw the sign. Every field is optional. */
export interface SignOptions {
  /** True (the default): the cell takes the plate gradient, and the
   *  `square` shape stands it on the ground. False: the edge and the glyph
   *  alone, for a lockup on a dark ground. */
  plate?: boolean;
  shape?: SignShape;
  tone?: SignTone;
  cut?: SignCut;
  /** The ink of the edge. The default is `[presentation.accent] color`. */
  accent?: string;
  /** The ink of the glyph. The default is `[presentation.accent] bright`. */
  bright?: string;
  /** The one ink of the `mono` tone. The default is the bright. */
  ink?: string;
  /** The prefix of every id the markup declares; the default is `sign`.
   *  Two plates in one document need two prefixes. */
  id?: string;
  /** True: the dev corner of the dev identity stands over the sign, in
   *  every tone and form (brand.md §15). */
  corner?: boolean;
  /** The ink of the keyline of the dev corner: the ground the sign stands
   *  on. The default is `[presentation.palette] ink`. */
  keyline?: string;
}

/** The identity a page wears: `prod`, or `dev` (brand.md §15). */
export type BrandIdentity = 'prod' | 'dev';

/** The name of the marker in the page head. Every page head that carries
 *  the sign writes `<meta name="ap-brand" content="prod">` (the define
 *  `brandmark` of `client/pages/_chrome.html.tmpl`), and the vhost of a dev
 *  environment and the dev server of Vite turn it to `dev`. */
export const BRAND_MARKER = 'ap-brand';

/** The identity of this page, read off the marker in its head. A page
 *  with no marker, and a test with no document, wear `prod`. */
export function brandIdentity(): BrandIdentity {
  if (typeof document === 'undefined') return 'prod';
  const m = document.querySelector<HTMLMetaElement>(`meta[name="${BRAND_MARKER}"]`);
  return m?.content === 'dev' ? 'dev' : 'prod';
}

/** True when this page wears the dev identity. */
export function devBrand(): boolean {
  return brandIdentity() === 'dev';
}

/** The path of a brand file of the dev identity: a file of the dev set
 *  stands under `brand/dev/` at its prod path (brand.md §15.3). */
export function devPath(path: string): string {
  return `/brand/dev/${path.replace(/^\/+/, '').replace(/^brand\//, '')}`;
}

/** The side of the sign's box, and its centre. */
export const SIGN_BOX = 24;
export const SIGN_CENTRE = SIGN_BOX / 2;

/** The cut table of `[presentation.sign]`: per cut, the radius and the edge
 *  of each shape, and the glyph and the counter at `SIGN.glyphR`. */
export const SIGN_CUTS = SIGN.cuts;

/** The plate gradient, top to foot: `[offset, colour]` stops. */
export const SIGN_PLATE = SIGN.plate;

/** The resolved geometry of one cut in one shape. */
export interface SignGeometry {
  cut: SignCut;
  shape: SignShape;
  /** The outer radius of the cell. */
  r: number;
  /** The width of the accent edge. */
  edge: number;
  /** The radius of the edge's centre line, `r - edge / 2`. The plate is
   *  filled to the same line, so the stroke covers the plate's rim. */
  ring: number;
  /** The scale of the glyph about the centre, `r / SIGN.glyphR`. */
  k: number;
  /** The six points of the cell at `ring`, from the top point clockwise. */
  hex: SignPoint[];
  /** The chevron and its counter, scaled by `k`. */
  glyph: SignPoint[];
  counter: SignPoint[];
}

/** A figure as the markup writes it: three decimals and no trailing zero.
 *  `sign.go` `signNum` writes the same string. */
function num(v: number): string {
  return String(Math.round(v * 1000) / 1000);
}

/** The pointy-top hexagon of radius `r` about the centre, from the top
 *  point clockwise: the map's own cell (ui-map-interface.md §1). It uses a
 *  square root and no sine, so Go and TypeScript compute the same bits. */
export function hexPoints(r: number): SignPoint[] {
  const dx = r * (Math.sqrt(3) / 2);
  const dy = r / 2;
  const c = SIGN_CENTRE;
  return [[c, c - r], [c + dx, c - dy], [c + dx, c + dy], [c, c + r], [c - dx, c + dy], [c - dx, c - dy]];
}

function scaled(pts: ReadonlyArray<SignPoint>, k: number): SignPoint[] {
  const c = SIGN_CENTRE;
  return pts.map(([x, y]) => [c + (x - c) * k, c + (y - c) * k] as const);
}

/** The geometry of one cut in one shape. */
export function signGeometry(cut: SignCut = 'full', shape: SignShape = 'round'): SignGeometry {
  const t = SIGN_CUTS[cut];
  const { r, edge } = t[shape];
  const k = r / SIGN.glyphR;
  return {
    cut, shape, r, edge, ring: r - edge / 2, k,
    hex: hexPoints(r - edge / 2),
    glyph: scaled(t.glyph, k),
    counter: scaled(t.counter, k),
  };
}

/** Points as an SVG `points` list. */
function pointList(pts: ReadonlyArray<SignPoint>): string {
  return pts.map(([x, y]) => `${num(x)},${num(y)}`).join(' ');
}

/** A closed polygon as an SVG path. */
function polygonPath(pts: ReadonlyArray<SignPoint>): string {
  return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${num(x)} ${num(y)}`).join(' ') + ' Z';
}

/** The path of the cell, at the edge's centre line. */
export function signHexPath(g: SignGeometry): string {
  return polygonPath(g.hex);
}

/** The path of the chevron alone. */
export function signChevronPath(g: SignGeometry): string {
  return polygonPath(g.glyph);
}

/** The path of the counter alone. */
export function signCounterPath(g: SignGeometry): string {
  return polygonPath(g.counter);
}

/** The glyph as the sign fills it: the chevron and the counter in one
 *  path, filled even-odd, so the counter is a hole. */
export function signGlyphPath(g: SignGeometry): string {
  return `${signChevronPath(g)} ${signCounterPath(g)}`;
}

interface Resolved {
  g: SignGeometry;
  plate: boolean;
  mono: boolean;
  accent: string;
  bright: string;
  ink: string;
  id: string;
  corner: boolean;
  keyline: string;
}

function resolve(o: SignOptions): Resolved {
  const bright = o.bright ?? ACCENT_BRIGHT_CSS;
  return {
    g: signGeometry(o.cut ?? 'full', o.shape ?? 'round'),
    plate: o.plate ?? true,
    mono: o.tone === 'mono',
    accent: o.accent ?? ACCENT_CSS,
    bright,
    ink: o.ink ?? bright,
    id: o.id ?? 'sign',
    corner: o.corner ?? false,
    keyline: o.keyline ?? SIGN.ground,
  };
}

/** The leg of the dev corner on a shape. */
function cornerLeg(shape: SignShape): number {
  return shape === 'square' ? SIGN.dev.squareLeg : SIGN.dev.roundLeg;
}

/** The path of the dev corner: the right triangle in the bottom-left
 *  corner of the box. */
export function signCornerPath(shape: SignShape = 'round'): string {
  const leg = cornerLeg(shape);
  return `M0 ${num(SIGN_BOX - leg)} L0 ${SIGN_BOX} L${num(leg)} ${SIGN_BOX} Z`;
}

/** The markup of the dev corner: the keyline under the fill, in a group
 *  of the class `devc`, so a page stylesheet can hide it. `sign.go`
 *  `signCorner` writes the same string for the ground of the page ink. */
export function signCorner(shape: SignShape = 'round', keyline: string = SIGN.ground): string {
  const d = signCornerPath(shape);
  return `<g class="devc"><path d="${d}" fill="none" stroke="${keyline}" stroke-width="${num(SIGN.dev.keyline)}" `
    + `stroke-linejoin="round"/><path d="${d}" fill="${SIGN.dev.ink}"/></g>`;
}

/** The inner markup of the sign in the 24-unit box, with no `<svg>`
 *  wrapper. `sign.go` `signMarkup` writes the same string. */
export function signSVG(o: SignOptions = {}): string {
  const s = resolve(o);
  const { g } = s;
  const poly = (fill: string, stroke: string): string =>
    `<polygon points="${pointList(g.hex)}" fill="${fill}" stroke="${stroke}" `
    + `stroke-width="${num(g.edge)}" stroke-linejoin="round"/>`;
  const glyph = (fill: string): string => `<path d="${signGlyphPath(g)}" fill="${fill}" fill-rule="evenodd"/>`;
  const corner = s.corner ? signCorner(g.shape, s.keyline) : '';
  if (s.mono) return poly('none', s.ink) + glyph(s.ink) + corner;
  if (!s.plate) return poly('none', s.accent) + glyph(s.bright) + corner;
  const id = `${s.id}-plate`;
  const stops = SIGN_PLATE.map(([at, c]) => `<stop offset="${num(at)}" stop-color="${c}"/>`).join('');
  return `<defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">${stops}</linearGradient></defs>`
    + (g.shape === 'square' ? `<rect width="24" height="24" fill="${SIGN.ground}"/>` : '')
    + poly(`url(#${id})`, s.accent)
    + glyph(s.bright)
    + corner;
}

/** A whole `<svg>` element of the sign at `px` CSS pixels. `attrs` is
 *  written into the tag as it stands, for a class or an `aria-*` pair. */
export function signSVGElement(px: number, o: SignOptions = {}, attrs = ''): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SIGN_BOX} ${SIGN_BOX}" width="${px}" height="${px}"`
    + `${attrs ? ` ${attrs}` : ''}>${signSVG(o)}</svg>`;
}

/** A 2D context the sign draws on. */
export type SignContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** The two paths of one cut in one shape, built once. */
const paths = new Map<string, { hex: Path2D; glyph: Path2D }>();

function pathsOf(g: SignGeometry): { hex: Path2D; glyph: Path2D } {
  const key = `${g.cut}:${g.shape}`;
  let p = paths.get(key);
  if (!p) {
    p = { hex: new Path2D(signHexPath(g)), glyph: new Path2D(signGlyphPath(g)) };
    paths.set(key, p);
  }
  return p;
}

/**
 * Draws the sign into the square `size` by `size` whose top left is
 * (`x`, `y`), in the context's own units. The plate gradient runs from the
 * top of the cell to its foot, as the SVG gradient of `signSVG` does over
 * the cell's box.
 */
export function drawSign(ctx: SignContext, x: number, y: number, size: number, o: SignOptions = {}): void {
  const s = resolve(o);
  const { g } = s;
  const p = pathsOf(g);
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / SIGN_BOX, size / SIGN_BOX);
  ctx.lineJoin = 'round';
  ctx.lineWidth = g.edge;
  if (s.mono) {
    ctx.strokeStyle = s.ink;
    ctx.stroke(p.hex);
    ctx.fillStyle = s.ink;
    ctx.fill(p.glyph, 'evenodd');
    if (s.corner) drawCorner(ctx, g.shape, s.keyline);
    ctx.restore();
    return;
  }
  if (s.plate) {
    if (g.shape === 'square') {
      ctx.fillStyle = SIGN.ground;
      ctx.fillRect(0, 0, SIGN_BOX, SIGN_BOX);
    }
    const grad = ctx.createLinearGradient(0, SIGN_CENTRE - g.ring, 0, SIGN_CENTRE + g.ring);
    for (const [at, c] of SIGN_PLATE) grad.addColorStop(at, c);
    ctx.fillStyle = grad;
    ctx.fill(p.hex);
  }
  ctx.strokeStyle = s.accent;
  ctx.stroke(p.hex);
  ctx.fillStyle = s.bright;
  ctx.fill(p.glyph, 'evenodd');
  if (s.corner) drawCorner(ctx, g.shape, s.keyline);
  ctx.restore();
}

/** The dev corner on a canvas, in the 24-unit box: the keyline, then the
 *  fill over its inner half. */
function drawCorner(ctx: SignContext, shape: SignShape, keyline: string): void {
  const path = new Path2D(signCornerPath(shape));
  ctx.lineJoin = 'round';
  ctx.lineWidth = SIGN.dev.keyline;
  ctx.strokeStyle = keyline;
  ctx.stroke(path);
  ctx.fillStyle = SIGN.dev.ink;
  ctx.fill(path);
}
